import * as React from 'react';
import { cn } from '../../lib/utils';

export interface FooterColumn {
  heading: string;
  links: Array<{ label: string; href: string; onClick?: () => void }>;
}

export interface LandingFooterProps extends React.HTMLAttributes<HTMLElement> {
  logo: React.ReactNode;
  tagline?: string;
  columns: FooterColumn[];
  copyright?: string;
  socials?: React.ReactNode;
}

export const LandingFooter = React.forwardRef<HTMLElement, LandingFooterProps>(
  (
    { className, logo, tagline, columns, copyright, socials, ...props },
    ref
  ) => (
    <footer
      ref={ref}
      className={cn('border-t border-border bg-muted/20 py-12', className)}
      {...props}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            {logo}
            {tagline && (
              <p className="text-sm text-muted-foreground mt-3">{tagline}</p>
            )}
          </div>

          {columns.map((col) => (
            <div key={col.heading}>
              <h4 className="font-semibold text-sm mb-3">{col.heading}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={link.onClick}
                      className="hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground">
          <p>{copyright || `© ${new Date().getFullYear()} All rights reserved.`}</p>
          {socials && <div className="flex gap-4 mt-3 md:mt-0">{socials}</div>}
        </div>
      </div>
    </footer>
  )
);

LandingFooter.displayName = 'LandingFooter';
